import { useColorModeValue } from 'jaen';
import { useIntl } from 'react-intl';
import { useLocalizeHref } from '../../contexts/locale';
import {
  Text,
  Box,
  Flex,
  Image,
  HStack,
  useDisclosure,
  IconButton,
  Grid,
  Container,
  Icon,
  Spacer,
  Button,
  LinkOverlay,
  LinkBox,
} from '@chakra-ui/react';
import { FC, useState, useEffect, type MouseEvent as ReactMouseEvent } from 'react';
import { useContactModal } from '../../services/contact';
import { FiSearch } from '@react-icons/all-files/fi/FiSearch';
import Netsnek from '../../gatsby-plugin-jaen/components/Netsnek';
import Logo from '../../gatsby-plugin-jaen/components/Logo';
import HamburgerMenuIcon, {
  THamburgerMenuIconStylerProps
} from '../AltHamburgerMenuIcon';
import LanguageSwitcher from '../LanguageSwitcher';
import SearchMenu from '../search-menu';
import { FadeIn } from '../FadeIn';

interface IAltTopNavProps {
  path: string;
  hamburgerIconProps?: THamburgerMenuIconStylerProps;
}

const AltTopNav: FC<IAltTopNavProps> = ({ path, hamburgerIconProps }) => {
  hamburgerIconProps = {
    color: 'red.500'
  };

  const contactModal = useContactModal();
  const handleOnContactClick = () => {
    contactModal.onOpen({
      meta: {}
    });
  };

  const intl = useIntl();
  const localizeHref = useLocalizeHref();
  const { open, onOpen, onClose } = useDisclosure();
  const [hamburgerClass, setHamburgerClass] = useState('');

  const openDrawer = () => {
    setHamburgerClass('open');
    onOpen();
  };

  const closeDrawer = () => {
    setHamburgerClass('');
    onClose();
  };

  const toggleMobileMenu = () => {
    if (hamburgerClass === 'open') closeDrawer();
    else openDrawer();
  };

  // The open menu fills the screen, and its section links stay on the same
  // page, so nothing closed it: the page jumped to the section behind the
  // menu. Closing it shrinks the header by most of a screen height, which
  // would move the section away again, so the scroll waits until the
  // header has collapsed (its transition takes 0.2s). On a page without
  // the section the link navigates to the home page as before.
  const goToSection = (event: ReactMouseEvent<HTMLAnchorElement>, id: string) => {
    const section = document.getElementById(id);
    closeDrawer();
    if (!section) return;

    event.preventDefault();
    window.setTimeout(() => {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      section.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      window.history.pushState(null, '', `#${id}`);
    }, 250);
  };

  // Handle window resize and close menu if window width is >= 768px (md breakpoint)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        closeDrawer();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Box
      //as={FadeIn}
      pos="relative"
      overflow="hidden"
      height={open ? 'calc(100vh + 15px)' : { base: '12vh', md: '15vh' }}
      minH={open ? '600px' : '100px'}
      transition="height 0.2s cubic-bezier(0.68, 0, 0.27, 1), min-height 0.2s cubic-bezier(0.68, 0, 0.27, 1)"
      //borderBottomRadius={'2xl'}
      //backgroundColor='blackAlpha.700'
    >
      <Box
        pos="relative"
        bg="brand.900"
        color="white"
        transition="height 0.2s cubic-bezier(0.68, 0, 0.27, 1), min-height 0.2s cubic-bezier(0.68, 0, 0.27, 1)"
        height={open ? 'max(600px, calc(100vh + 15px))' : '0'}
        minH={open ? 'fit-content' : '0'}
        width="100%"
        overflow="hidden"
      >
        <Grid
          maxW="8xl"
          minH="max(600px, calc(100vh + 15px))"
          // Set the first row height to 15vh
          templateRows={{
            base: 'auto repeat(6, 1fr)',
            md: 'auto repeat(3, 1fr)'
          }}
          templateColumns={{ base: '1fr', md: '1fr 1fr' }}
          templateAreas={{
            base: `"empty" "services" "team" "portfolio" "blog" "offices"`,
            md: `"empty empty"
                         "services team"
                         "portfolio blog"
                         "offices ."`
          }}
          //gap={4}
          fontSize={{ base: 'xx-large', md: 'xxx-large' }}
          h="full"
          w="full"
          asChild
        ><Container>
            <Box
              gridArea="empty"
              h={{ base: '12vh', md: '15vh' }}
              minH={{ base: '100px', md: '100px' }}
            />
            <LinkBox
              gridArea="services"
              display="flex"
              pl={{ base: '8', md: '16' }}
              alignItems="center"
              borderWidth="1px"
              borderStyle="solid"
              borderRight="1px"
              borderLeft="0"
              borderBottom="0"
              borderColor={useColorModeValue('brand.800', 'gray.600')}
              transition="color 0.2s"
              _hover={{
                color: 'brand.500'
              }}
            >
              <LinkOverlay href={localizeHref('/#angebot')} onClick={event => goToSection(event, 'angebot')}>
              {intl.formatMessage({ id: 'NavOffer', defaultMessage: 'Angebot' })}
            </LinkOverlay>
            </LinkBox>
            <LinkBox
              gridArea="team"
              display="flex"
              pl={{ base: '8', md: '16' }}
              alignItems="center"
              borderWidth="1px"
              borderStyle="solid"
              borderBottom="0"
              borderRight="0"
              borderLeft={{ base: '1px', md: '0px' }}
              borderColor={useColorModeValue('brand.800', 'gray.600')}
              transition="color 0.2s"
              _hover={{
                color: 'brand.500'
              }}
            >
              <LinkOverlay href={localizeHref('/#ablauf')} onClick={event => goToSection(event, 'ablauf')}>
              {intl.formatMessage({ id: 'NavProcess', defaultMessage: 'Ablauf' })}
            </LinkOverlay>
            </LinkBox>
            <LinkBox
              gridArea="portfolio"
              display="flex"
              pl={{ base: '8', md: '16' }}
              alignItems="center"
              borderWidth="1px"
              borderStyle="solid"
              borderRight="1px"
              borderLeft="0"
              borderBottom="0"
              borderColor={useColorModeValue('brand.800', 'gray.600')}
              transition="color 0.2s"
              _hover={{
                color: 'brand.500'
              }}
            >
              <LinkOverlay href={localizeHref('/#praxis')} onClick={event => goToSection(event, 'praxis')}>
              {intl.formatMessage({ id: 'NavPractice', defaultMessage: 'Praxis' })}
            </LinkOverlay>
            </LinkBox>
            <LinkBox
              gridArea="blog"
              display="flex"
              pl={{ base: '8', md: '16' }}
              alignItems="center"
              borderWidth="1px"
              borderStyle="solid"
              borderBottom="1px"
              borderRight="0"
              borderLeft={{ base: '1px', md: '0px' }}
              borderColor={useColorModeValue('brand.800', 'gray.600')}
              transition="color 0.2s"
              _hover={{
                color: 'brand.500'
              }}
            >
              <LinkOverlay href={localizeHref('/docs/')}>
              {intl.formatMessage({ id: 'NavBlog', defaultMessage: 'Blog' })}
            </LinkOverlay>
            </LinkBox>
            <Box
              gridArea="offices"
              pt={{ base: '8' }}
              pl={{ base: '8', md: '16' }}
              borderWidth="1px"
              borderTop="1px"
              borderBottom="0"
              borderRight={{ base: '1px', md: '0px' }}
              borderLeft="0"
              borderStyle="solid"
              borderColor={{ base: 'transparent', md: useColorModeValue('brand.800', 'gray.600') }}
            >
              {/* Office information here */}
              <Text color="white" fontWeight="bold" fontSize="lg" pb="4">
                {intl.formatMessage({
                  id: 'NavLocationTitle',
                  defaultMessage: 'Im Herzen von Wien'
                })}
              </Text>
              {/* <Text color="white" fontSize="md">
                Hauptquartier
              </Text> */}
              <Text color="gray.400" fontSize="md">
                Löwengasse 28/22
              </Text>
              <Text color="gray.400" fontSize="md">
                {intl.formatMessage({
                  id: 'NavLocationCity',
                  defaultMessage: '1030 Wien, Österreich'
                })}
              </Text>
            </Box>
          </Container></Grid>
      </Box>
      {/* The rest of your AltTopNav and navigation components */}
      <Box pos="absolute" top="0" left="0" w="100%">
        <Container maxW="8xl" pointerEvents="auto">
          <HStack
            h={{ base: '12vh', md: '15vh' }}
            minH="100px"
            alignItems={'center'}
            px={{ base: '4', md: '8' }}
            py={{ base: '2', md: '4' }}
            justifyContent="space-between"
          >
            <LinkBox height="100%" flex="2" mr="4" display="flex">
              <LinkOverlay href={localizeHref('/')} color="white">
                {/* <Logo height="100%" color="#9d1bb0" /> */}
                <Logo height="100%" />
                {/* <Image height="100%" src="/content/nadinefontlogo2.png" /> */}
                {/* <Text py="5" fontSize="4xl" fontWeight="bold" color="#9d1bb0">
                  Nadine Hauswirth
                </Text> */}
              </LinkOverlay>
            </LinkBox>
            <Flex alignItems="center" flex="1" justifyContent="flex-end">
              {/* <Button
                variant="ghost"
                ml={1}
                filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                onClick={() => {
                  // if not on the homepage, redirect to the homepage first
                  if (path !== '/docs') {
                    window.location.href = '/docs';
                  }

                  // const element = document.getElementById('meine_kunden');
                  // if (element) {
                  //   element.scrollIntoView({ behavior: 'smooth' });
                  // }
                }}
                fontSize="sm"
                fontWeight="semibold"
                color="#273E53"
                display={{ base: 'none', lg: 'block' }}
              >
                Über mich
              </Button>
              <Button
                variant="ghost"
                ml={1}
                filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                onClick={() => {
                  // if not on the homepage, redirect to the homepage first
                  if (path !== '/') {
                    window.location.href = '/';
                  }

                  const element = document.getElementById('meine_beratung');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                fontSize="sm"
                fontWeight="semibold"
                color="#273E53"
                display={{ base: 'none', lg: 'block' }}
              >
                Kurse & Webinare
              </Button>
              <Button
                variant="ghost"
                ml={1}
                filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                onClick={() => window.location.href = '/recipes'}
                fontSize="sm"
                fontWeight="semibold"
                color="#273E53"
                display={{ base: 'none', lg: 'block' }}
              >
                Rezepte
              </Button>
              <Button
                variant="ghost"
                ml={1}
                filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                onClick={() => window.location.href = '/docs'}
                fontSize="sm"
                fontWeight="semibold"
                color="#273E53"
                display={{ base: 'none', lg: 'block' }}
              >
                Blog
              </Button> */}
              <LanguageSwitcher />
              {/* No display override: SearchButton lays itself out as flex,
                  and block would drop the 8px flex gap between the keycap and
                  the label — the white face in the opened bar is flex, and
                  both faces must measure identically for the overlay to sit
                  exactly on this control. */}
              <SearchMenu
                ml={4}
                boxSizing={'border-box'}
                borderWidth={{ base: 0, lg: 2 }}
                borderColor='#7f188c'
                color={{ base: '#7f188c', md: '#7f188c' }}
                fontWeight={'semibold'}
                opacity="0.7"
                _hover={{
                  opacity: "1",
                  borderColor: 'brand.800',
                  bg: { base: 'brand.800', md: 'transparent' }
                }}
              />
              <Button
                ml={2}
                filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                onClick={handleOnContactClick}
                fontSize="sm"
                fontWeight="semibold"
                bgColor="#7f188c"
                color="white"
                _hover={{ bgColor: 'brand.700' }}
                _active={{ bgColor: 'brand.700' }}
                display={{ base: 'none', lg: 'block' }}
              >
                {intl.formatMessage({ id: 'NavContact', defaultMessage: 'Kontakt' })}
              </Button>
              {/* <Button
                  ml={4}
                  filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                  onClick={handleOnContactClick}
                  fontSize="sm"
                  fontWeight="semibold"
                  color="white"
                  display={{base: 'none', md: 'block'}}>
                  Login
                </Button> */}
              <IconButton
                variant="ghost"
                // display={{ base: 'block', lg: 'none' }}
                ml={4}
                filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                //variant="ghost"
                fontWeight={'bold'}
                aria-label={open ? 'Close menu' : 'Open menu'}
                onClick={toggleMobileMenu}
              >
                {/* v3 dropped the `icon` prop: an IconButton takes its glyph
                    as a child like every other button. */}
                <HamburgerMenuIcon
                  handleClick={toggleMobileMenu}
                  wrapperProps={{ className: hamburgerClass }}
                  iconProps={{
                    // ...hamburgerIconProps,
                    //boxSize: '6',
                    //boxSize: '100%',
                    backgroundColor: open ? 'white' : '#7f188c'
                  }}
                />
              </IconButton>
            </Flex>
          </HStack>
        </Container>
      </Box>
      <Box
        pointerEvents="none"
        pos="absolute"
        top="0"
        left="0"
        transition="height 0.2s cubic-bezier(0.68, 0, 0.27, 1)"
        h={open ? 'max(600px, 100vh)' : '0'} // Adjust according to your AltTopNav's initial height
        w="100%"
        overflow="hidden"
      >
        <Container maxW="8xl" pointerEvents="auto">
          <HStack
            h={{ base: '12vh', md: '15vh' }}
            minH="100px"
            alignItems={'center'}
            px={{ base: '4', md: '8' }}
            py={{ base: '2', md: '4' }}
            justifyContent="space-between"
          >
            <LinkBox height="100%" flex="2" mr="4" display="flex">
              <LinkOverlay href={localizeHref('/')} color="white">
                <Logo height="100%" color="white" />
                {/* <Image height="100%" src="/content/nadinefontlogo2.png" filter={"invert(1)"} /> */}
                {/* <Text py="5" fontSize="4xl" fontWeight="bold" color="#ffffff">
                  Nadine Hauswirth
                </Text> */}
              </LinkOverlay>
            </LinkBox>
            <Flex alignItems="center" flex="1" justifyContent="flex-end">
              {/* In the opened menu a white face slides over each control,
                  otherwise the language switcher would be the only violet
                  thing left standing on the dark panel. */}
              <LanguageSwitcher
                bgColor="white"
                color="black"
                opacity={1}
                _hover={{ bgColor: 'gray.100', color: 'black', opacity: 1 }}
                _active={{ bgColor: 'gray.200', color: 'black' }}
              />
              {/* Solid white with black content, like every other control in
                  this bar. It used to go transparent with a white outline from
                  lg upwards, which is what made its keycap unreadable: the cap
                  is styled for a light surface and was sitting on the black
                  panel. */}
              <SearchMenu
                boxSizing={'border-box'}
                borderWidth={{ base: 0, lg: 2 }}
                borderColor="white"
                bgColor="white"
                color="black"
                fontWeight={'semibold'}
                _hover={{
                  bgColor: 'gray.100',
                  color: 'black',
                  borderColor: 'gray.100'
                }}
                _active={{
                  bgColor: 'gray.200',
                  color: 'black',
                  borderColor: 'gray.200'
                }}
                ml={4}
              />
              <Button
                ml={2}
                _hover={{ bgColor: 'gray.100', color: 'black' }}
                _active={{ bgColor: 'gray.200', color: 'black' }}
                filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                //variant="ghost"
                onClick={handleOnContactClick}
                fontSize="sm"
                fontWeight="semibold"
                bgColor="white"
                color="black"
                display={{ base: 'none', lg: 'block' }}
              >
                {intl.formatMessage({ id: 'NavContact', defaultMessage: 'Kontakt' })}
              </Button>
              {/* <Button
                  ml={4}
                  _hover={{
                    bg: 'brand.500'
                  }}
                  filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                  //variant="ghost"
                  onClick={handleOnContactClick}
                  fontSize="sm"
                  fontWeight="semibold"
                  bg="white"
                  color="black"
                  display={{base: 'none', md: 'block'}}>
                  Login
                </Button> */}
              {/* White, like the three controls beside it. It was brand.900,
                  which is #18011a: a near-black button on a near-black panel,
                  so the close control had no visible shape at all and only its
                  white bars showed. The bars go black to sit on the new face. */}
              <IconButton
                ml={4}
                _hover={{ bgColor: 'gray.100' }}
                _active={{ bgColor: 'gray.200' }}
                filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                fontWeight={'bold'}
                aria-label={open ? 'Close menu' : 'Open menu'}
                onClick={toggleMobileMenu}
                bgColor="white"
                color="black"
              >
                <HamburgerMenuIcon
                  // handleClick={toggleMobileMenu}
                  wrapperProps={{ className: hamburgerClass }}
                  iconProps={{
                    // ...hamburgerIconProps,
                    //boxSize: '6',
                    //boxSize: '100%',
                    backgroundColor: 'black'
                  }}
                />
              </IconButton>
            </Flex>
          </HStack>
        </Container>
      </Box>
    </Box>
  );
};

export default AltTopNav;
